<template>
  <v-group :config="groupConfig">
    <v-rect v-if="debug" :config="rectConfig" />
    <v-image v-if="image" ref="image" :config="imageConfig" />
  </v-group>
</template>

<script>
/**
 * @enum
 * @readonly
 */
import ConfiguratorTextureCanvasLayer, {
  DASH_WIDTH,
} from '../../mixins/ConfiguratorTextureCanvasLayer';
import { loadImage } from '../../utils/ImageUtils';
import { screenPrintingFilter } from '../../utils/StageUtils';
import PromiseHelper from '../../utils/PromiseHelper';

const PromiseKey = Object.freeze({
  FILTER: 0,
});

export default {
  name: 'ConfiguratorTextureCanvasLayerImage',
  mixins: [ConfiguratorTextureCanvasLayer],
  props: {
    url: {
      type: String,
      default: undefined,
    },
    color: {
      type: String,
      default: undefined,
    },
    innerWidth: { type: Number, default: undefined },
    innerHeight: { type: Number, default: undefined },
  },
  data() {
    return {
      imagePromise: null,
      isImageLoaded: false,
      image: null,
      promiseHelper: new PromiseHelper(),
    };
  },
  computed: {
    dashOffset() {
      return DASH_WIDTH;
    },
    rectStroke() {
      return 'blue';
    },
    /**
     *
     * @return {ImageConfig}
     */
    imageConfig() {
      const groupNodeCommonConfig = this.groupNodeCommonConfig;

      /** @type {TextConfig} */
      const config = {
        x: this.innerX || groupNodeCommonConfig.x,
        y: this.innerY || groupNodeCommonConfig.y,
        rotation: this.innerRotation || this.rotation,
        width: this.innerWidth || groupNodeCommonConfig.width,
        height: this.innerHeight || groupNodeCommonConfig.height,
      };

      if (this.innerScaleX) {
        config.scaleX = this.innerScaleX;
      }

      if (this.innerScaleY) {
        config.scaleY = this.innerScaleY;
      }

      if (this.color) {
        config.filters = [this.screenPrintingFilter];
      }

      if (this.image && this.isImageLoaded) {
        config.image = this.image;
      }

      return config;
    },
    screenPrintingFilter() {
      return screenPrintingFilter(this.color, 'rgba(255, 255, 255, 0)');
    },
  },
  watch: {
    async imageConfig() {
      await this.renderImage();
    },
    async color() {
      await this.renderImage();
    },
  },
  created() {
    this.promiseHelper.request(PromiseKey.FILTER);

    this.$watch(
      'url',
      async function (url) {
        if (this.isImageLoaded) {
          this.isImageLoaded = false;
        }

        if (this.url) {
          this.imagePromise = loadImage(url).catch(e => {
            console.error(`Unable to load image.`);
            console.error(e);
          });

          this.image = await this.imagePromise;
        } else {
          this.imagePromise = Promise.resolve();
          this.promiseHelper.resolve(PromiseKey.FILTER);
        }

        this.isImageLoaded = true;
      },
      { immediate: true }
    );
  },
  methods: {
    /**
     *
     * @return {Promise<*[]>}
     */
    async layerReady() {
      // Wait for $nextTick so watchers are called before
      // I don't really understand why it works
      return this.$nextTick()
        .then(() =>
          Promise.all([
            this.imagePromise,
            this.promiseHelper.get(PromiseKey.FILTER),
          ])
        )
        .then(() => {
          // Ensure image is rendered by requesting a canvas draw
          if (this.$refs.image) {
            const imageNode = this.$refs.image.getNode();
            imageNode.getLayer().batchDraw();
          }
        });
    },
    async renderImage() {
      await this.applyFilters();
    },
    async applyFilters() {
      if (!this.promiseHelper.isPending(PromiseKey.FILTER)) {
        this.promiseHelper.request(PromiseKey.FILTER);
      }

      // Waiting for the next event loop
      // TODO Find out why $nextTick alone doesn't work
      await this.$nextTick();

      const imageRef = this.$refs.image;

      // imageRef can be undefined when image property is null
      if (imageRef) {
        const imageNode = imageRef.getNode();

        // See https://stackoverflow.com/a/62836435/1224564
        imageNode.cache({ pixelRatio: 1 });

        imageNode.getLayer().batchDraw();
      }

      this.promiseHelper.resolve(PromiseKey.FILTER);
    },
  },
};
</script>
